// @ts-check
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';

import { useEnvelope, useEnvelopesRoutes } from '@/composables/envelopes';
import i18n from '@/locales';

/**
 * How long to delay setting the isBusyOnNext or
 * isBusyOnSave values, in milliseconds
 */
const BUSY_DELAY = 1000;

/**
 * Envelopes creation flow/steps route stuff
 * Temporary, will be updated when tanstack query is implemented
 *
 * @param {string} [action]
 * @param {import('vue').ComputedRef} [update]
 * @param {import('vue').ComputedRef<boolean>} [valid]
 * @param {(error:any) => void} [onError]
 */
export function useEnvelopesStepsNavigation(action, update, valid, onError) {
  const validOrTrue = computed(() => (valid ? valid.value : true));
  const router = useRouter();
  const currentRoute = useRoute();

  const {
    progressUpdate, prevRoute, nextRoute, isCreateRoute, isStepsRoute,
  } = useEnvelopesRoutes();

  // @TODO replace with query/s
  const { createAction, updateProgress, updateAction } = {
    createAction: () => {},
    updateProgress: () => {},
    updateAction: () => {},
  };

  /**
   * @param {import('vue-router').RawLocation} route
   * @param {boolean} [saveProgress]
   */
  async function doUpdate(route, saveProgress = true) {
    if (action && update.value) {
      try {
        await updateAction({
          fingerprint: currentRoute.params.fingerprint,
          data: update.value,
        });
      } catch (error) {
        onError(error);

        return;
      }
    }

    if (saveProgress && progressUpdate.value) {
      updateProgress({
        fingerprint: currentRoute.params.fingerprint,
        data: progressUpdate.value,
      });
    }

    await router.push(route);
  }

  const onBack = computed(() => {
    if (!prevRoute.value) {
      return undefined;
    }

    return () => router.push(prevRoute.value);
  });

  let isBusyTimeout;
  const isBusyOnSave = ref(false);
  const isBusyOnNext = ref(false);

  function clearBusy() {
    isBusyOnSave.value = false;
    isBusyOnNext.value = false;
    clearTimeout(isBusyTimeout);
  }

  const onSave = computed(() => {
    if (!isStepsRoute.value) {
      return undefined;
    }

    return async () => {
      try {
        isBusyTimeout = setTimeout(() => {
          isBusyOnSave.value = true;
        }, BUSY_DELAY);

        await doUpdate({ name: 'Envelopes' }, false);
      } finally {
        clearBusy();
      }
    };
  });

  const onNext = computed(() => async () => {
    try {
      isBusyTimeout = setTimeout(() => {
        isBusyOnNext.value = true;
      }, BUSY_DELAY);

      if (isCreateRoute.value) {
        const envelope = await createAction(update.value);
        await router.push({
          ...nextRoute.value,
          params: { fingerprint: envelope.fingerprint },
        });
      } else {
        await doUpdate(nextRoute.value);
      }
    } finally {
      clearBusy();
    }
  });

  const next = computed(() => ({
    action: onNext.value,
    busy: isBusyOnNext.value,
    disabled: !validOrTrue.value || isBusyOnSave.value,
  }));

  const save = computed(() => ({
    action: onSave.value,
    busy: isBusyOnSave.value,
    disabled: !validOrTrue.value || isBusyOnNext.value,
  }));

  const back = computed(() => ({
    action: onBack.value,
    disabled: isBusyOnNext.value || isBusyOnSave.value,
  }));

  return {
    next,
    save,
    back,
  };
}

export function useEnvelopesProgress() {
  const route = useRoute();
  const {
    stepsRoutes, currentIndex, isEditRoute, isCreateRoute,
  } = useEnvelopesRoutes();

  const { isSent } = useEnvelope(route.params.fingerprint);

  const stepIndex = computed(() =>
    (isCreateRoute.value ? 0 : currentIndex.value));

  const steps = computed(() => {
    if (isSent.value || isEditRoute.value) {
      return undefined;
    }

    return stepsRoutes.map((stepRoute, i) => ({
      title: i18n.t(`ui.envelopes.create.stepTitles.${stepRoute.name}`),
      current: i === stepIndex.value,
      disabled: i > stepIndex.value,
      clickable: i > stepIndex.value,
    }));
  });

  return { steps, stepsRoutes };
}
